// frontend/src/components/Layout/AppShell.jsx
// SignalIQ authenticated app shell
// Enterprise RBAC patch:
// - Blocks testers and non-admin users from Admin Control, AI Workforce, Model Lab,
//   and Model Governance routes at the shell level.
// - Keeps Sidebar, Topbar, and Aurelia voice assistant. Email verification is handled during registration/login, not as a persistent workspace banner.
// - Retains mojibake cleanup for text rendering stability.
// - Keeps support access clean and minimal across authenticated pages.

import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Link,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import Sidebar from "../Sidebar";
import Topbar from "../Topbar";
import { useAuth } from "../../context/AuthContext";

const SignalIQVoice = lazy(() => import("../SignalIQVoice"));

function IdleOnly({ children }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      setReady(true);
      return undefined;
    }

    let cancelled = false;

    const markReady = () => {
      if (!cancelled) setReady(true);
    };

    let idleId = null;
    let timerId = null;

    if ("requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(markReady, { timeout: 2500 });
    } else {
      timerId = window.setTimeout(markReady, 1200);
    }

    return () => {
      cancelled = true;
      if (idleId !== null && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timerId !== null) {
        window.clearTimeout(timerId);
      }
    };
  }, []);

  return ready ? children : null;
}

const MOJIBAKE_REPLACEMENTS = [
  ["â€¦", "…"],
  ["â€”", "—"],
  ["â€“", "–"],
  ["â€˜", "‘"],
  ["â€™", "’"],
  ["â€œ", "“"],
  ["â€", "”"],
  ["â€", "”"],
  ["Â·", "·"],
  ["Â ", " "],
  ["Â", ""],
  ["Ã—", "×"],
  ["Ã©", "é"],
  ["Ã¨", "è"],
  ["Ã¡", "á"],
  ["Ã­", "í"],
  ["Ã³", "ó"],
  ["Ãº", "ú"],
  ["Ã±", "ñ"],
  ["ï»¿", ""],
];

function cleanText(value) {
  if (typeof value !== "string" || value.length === 0) return value;

  let cleaned = value;
  for (const [bad, good] of MOJIBAKE_REPLACEMENTS) {
    cleaned = cleaned.split(bad).join(good);
  }
  return cleaned;
}

function cleanElementAttributes(element) {
  if (!element || element.nodeType !== Node.ELEMENT_NODE) return;

  const attrs = [
    "placeholder",
    "title",
    "aria-label",
    "alt",
    "value",
    "data-placeholder",
  ];

  for (const attr of attrs) {
    if (!element.hasAttribute?.(attr)) continue;
    const current = element.getAttribute(attr);
    const next = cleanText(current);
    if (next !== current) element.setAttribute(attr, next);
  }

  if ("placeholder" in element && typeof element.placeholder === "string") {
    const next = cleanText(element.placeholder);
    if (next !== element.placeholder) element.placeholder = next;
  }

  if ("value" in element && typeof element.value === "string") {
    const next = cleanText(element.value);
    if (next !== element.value) element.value = next;
  }
}

function cleanNodeText(root) {
  if (!root || typeof document === "undefined") return;

  if (root.nodeType === Node.ELEMENT_NODE) {
    cleanElementAttributes(root);
    root.querySelectorAll?.("*").forEach(cleanElementAttributes);
  }

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes = [];

  while (walker.nextNode()) {
    textNodes.push(walker.currentNode);
  }

  textNodes.forEach((node) => {
    const nextValue = cleanText(node.nodeValue);
    if (nextValue !== node.nodeValue) node.nodeValue = nextValue;
  });
}

function useMojibakeCleanup() {
  useEffect(() => {
    if (typeof document === "undefined" || typeof MutationObserver === "undefined") {
      return undefined;
    }

    let frameId = null;
    let idleId = null;
    const pendingNodes = new Set();

    const flush = () => {
      frameId = null;

      const nodes = Array.from(pendingNodes);
      pendingNodes.clear();

      for (const node of nodes) {
        if (!node) continue;

        if (node.nodeType === Node.TEXT_NODE) {
          const nextValue = cleanText(node.nodeValue);
          if (nextValue !== node.nodeValue) node.nodeValue = nextValue;
          continue;
        }

        if (node.nodeType === Node.ELEMENT_NODE) {
          cleanNodeText(node);
        }
      }
    };

    const schedule = (node) => {
      if (node) pendingNodes.add(node);

      if (frameId !== null) return;

      frameId = window.requestAnimationFrame(flush);
    };

    // Initial cleanup should not block first paint.
    const initialCleanup = () => schedule(document.body);

    if ("requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(initialCleanup, { timeout: 1500 });
    } else {
      window.setTimeout(initialCleanup, 0);
    }

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "attributes") {
          schedule(mutation.target);
          continue;
        }

        if (mutation.type === "characterData") {
          schedule(mutation.target);
          continue;
        }

        mutation.addedNodes.forEach(schedule);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: [
        "placeholder",
        "title",
        "aria-label",
        "alt",
        "value",
        "data-placeholder",
      ],
    });

    return () => {
      observer.disconnect();

      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }

      if (idleId !== null && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleId);
      }

      pendingNodes.clear();
    };
  }, []);
}

function EmailVerifyBanner({ user, onResend }) {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const handleResend = useCallback(async () => {
    if (sending || sent) return;

    setSending(true);
    try {
      await onResend();
      setSent(true);
    } finally {
      setSending(false);
    }
  }, [sending, sent, onResend]);

  return (
    <div className="email-verify-banner" role="alert">
      <div className="email-verify-banner__text">
        <strong>Verify your email</strong>
        <span>
          A verification link was sent to <strong>{user?.email}</strong>. Check
          your inbox to unlock all features.
        </span>
      </div>

      {!sent ? (
        <button type="button" onClick={handleResend} disabled={sending}>
          {sending ? "Sending…" : "Resend link"}
        </button>
      ) : (
        <span className="email-verify-banner__sent">Email sent</span>
      )}
    </div>
  );
}

function matchesProtectedRoute(pathname, routes) {
  const current = String(pathname || "").toLowerCase();

  return routes.some((route) => {
    const cleanRoute = String(route || "").toLowerCase();
    return current === cleanRoute || current.startsWith(`${cleanRoute}/`);
  });
}

const SupportAccessBar = React.memo(function SupportAccessBar({ pathname }) {
  const currentPath = String(pathname || "").toLowerCase();

  // The full support page already has its own hero and contact actions.
  // Hiding the compact access button there prevents duplicate support headers.
  if (currentPath === "/support" || currentPath.startsWith("/support/")) {
    return null;
  }

  return (
    <div
      className="support-access-bar"
      aria-label="SignalIQ support access"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        padding: "10px 18px 0",
        pointerEvents: "none",
      }}
    >
      <Link
        to="/support"
        title="Open Support Center"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          border: "1px solid rgba(255,255,255,0.10)",
          background: "rgba(255,255,255,0.045)",
          color: "var(--txt-mid)",
          borderRadius: 10,
          padding: "7px 10px",
          fontSize: 12,
          fontWeight: 700,
          lineHeight: 1,
          textDecoration: "none",
          whiteSpace: "nowrap",
          pointerEvents: "auto",
          boxShadow: "0 8px 22px rgba(2,6,23,0.18)",
          transition: "border-color 160ms ease, background 160ms ease, color 160ms ease, transform 160ms ease",
        }}
        onMouseEnter={(event) => {
          event.currentTarget.style.borderColor = "rgba(59,130,246,0.30)";
          event.currentTarget.style.background = "rgba(59,130,246,0.08)";
          event.currentTarget.style.color = "var(--txt-hi)";
          event.currentTarget.style.transform = "translateY(-1px)";
        }}
        onMouseLeave={(event) => {
          event.currentTarget.style.borderColor = "rgba(255,255,255,0.10)";
          event.currentTarget.style.background = "rgba(255,255,255,0.045)";
          event.currentTarget.style.color = "var(--txt-mid)";
          event.currentTarget.style.transform = "translateY(0)";
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 18,
            height: 18,
            borderRadius: 6,
            display: "grid",
            placeItems: "center",
            background: "rgba(59,130,246,0.12)",
            color: "var(--blue-lt)",
            fontSize: 12,
            fontWeight: 900,
            lineHeight: 1,
          }}
        >
          ?
        </span>
        Support
      </Link>
    </div>
  );
});

export default function AppShell() {
  const {
    user,
    resendVerification,
    canAccessAdmin,
    canAccessGovernance,
    canAccessModelLab,
    isTesterAccount,
  } = useAuth();

  const location = useLocation();

  useMojibakeCleanup();

  const adminRoutes = useMemo(
    () => [
      "/admin",
      "/admin-control",
      "/admin-control-center",
      "/ai-workforce",
      "/workspace-admin",
      "/user-management",
      "/audit-logs",
      "/system-logs",
    ],
    []
  );

  const governanceRoutes = useMemo(
    () => [
      "/model-governance",
      "/governance",
    ],
    []
  );

  const modelLabRoutes = useMemo(
    () => [
      "/model-lab",
    ],
    []
  );

  const pathname = String(location?.pathname || "").toLowerCase();

  const hitsAdminRoute = matchesProtectedRoute(pathname, adminRoutes);
  const hitsGovernanceRoute = matchesProtectedRoute(pathname, governanceRoutes);
  const hitsModelLabRoute = matchesProtectedRoute(pathname, modelLabRoutes);

  const isTester = Boolean(isTesterAccount?.());

  // Testers should never access the admin/governance/model-lab layer, even if
  // a route is manually typed in the browser.
  if (
    isTester &&
    (hitsAdminRoute || hitsGovernanceRoute || hitsModelLabRoute)
  ) {
    return <Navigate to="/dashboard" replace />;
  }

  if (hitsAdminRoute && !canAccessAdmin?.()) {
    return <Navigate to="/dashboard" replace />;
  }

  if (hitsGovernanceRoute && !canAccessGovernance?.()) {
    return <Navigate to="/dashboard" replace />;
  }

  if (hitsModelLabRoute && !canAccessModelLab?.()) {
    return <Navigate to="/dashboard" replace />;
  }

  // Production rule: email verification is completed during registration/login.
  // Do not show a persistent workspace banner unless the backend explicitly
  // requires it for the current authenticated session.
  const showVerifyBanner = Boolean(user?.requires_email_verification === true);

  return (
    <div className="app-shell app-shell--fullbleed">
      <Sidebar />

      <main className="nfi-main" role="main">
        <Topbar />

        <SupportAccessBar pathname={pathname} />

        {showVerifyBanner ? (
          <EmailVerifyBanner
            user={user}
            onResend={resendVerification || (() => Promise.resolve())}
          />
        ) : null}

        <div className="nfi-scroll">
          <div className="nfi-page nfi-page--fullbleed">
            <Outlet />
          </div>
        </div>
      </main>

      <IdleOnly>
        <Suspense fallback={null}>
          <SignalIQVoice />
        </Suspense>
      </IdleOnly>
    </div>
  );
}
